import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Check, Search, Coins, Gift, Package, Calendar, Ban } from "lucide-react";
import { format } from "date-fns";

interface User {
  id: number;
  username: string;
  email?: string;
}

export default function AwardPlayersTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [awardType, setAwardType] = useState<"CURRENCY" | "DECK" | "SHOP_ITEM" | "">("");
  const [currencyType, setCurrencyType] = useState<"COINS" | "GEMS" | "">("");
  const [amount, setAmount] = useState<number | "">("");
  const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null);
  const [selectedShopItemId, setSelectedShopItemId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Get all users
  const { data: allUsers } = useQuery({
    queryKey: ["/api/admin/users"],
    queryFn: async () => {
      return apiRequest<User[]>("/api/admin/users");
    },
  });

  // Get available decks
  const { data: decks, isLoading: decksLoading } = useQuery({
    queryKey: ["/api/manager/decks"],
    queryFn: () => apiRequest<any[]>("/api/manager/decks"),
  });

  // Get available shop items
  const { data: shopItems, isLoading: shopItemsLoading } = useQuery({
    queryKey: ["/api/currency/shop/all"],
    queryFn: () => apiRequest<any[]>("/api/currency/shop/all"),
  });

  // Filter users based on search query
  const filteredUsers = allUsers && searchQuery
    ? allUsers.filter((user: User) => {
        // Search filter
        return [
          user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          user.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);
      })
    : [];

  // Award granting mutation
  // Get award history
  const { data: awardHistory, isLoading: isHistoryLoading, refetch: refetchHistory } = useQuery({
    queryKey: ["/api/awards/admin/awards/history"],
    queryFn: () => apiRequest<{ success: boolean, awards: any[] }>("/api/awards/admin/awards/history"),
  });

  const awardMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest<any>("/api/awards/admin/awards", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Award granted successfully",
          description: `The player has been awarded successfully.`,
        });
        resetForm();
        refetchHistory(); // Refresh award history after successful award
      } else {
        toast({
          title: "Error granting award",
          description: data.message || "An unknown error occurred",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to grant award",
        variant: "destructive",
      });
    },
  });

  const handlePlayerSelect = (userId: number, username: string) => {
    setSelectedPlayerId(userId);
    setSearchQuery(username);
  };

  const resetForm = () => {
    setSelectedPlayerId(null);
    setAwardType("");
    setCurrencyType("");
    setAmount("");
    setSelectedDeckId(null);
    setSelectedShopItemId(null);
    setReason("");
  };

  // Format award details for display
  const formatAwardDetails = (award: any) => {
    switch (award.awardType) {
      case 'CURRENCY':
        return `${award.amount} ${award.currencyType.toLowerCase()}`;
      case 'DECK':
        return `Deck: ${award.deck?.name || "Unknown deck"}`;
      case 'SHOP_ITEM':
        return `Item: ${award.shopItem?.name || "Unknown item"}`;
      default:
        return "Unknown award type";
    }
  };

  // Format award status
  const formatAwardStatus = (award: any) => {
    return award.isClaimed 
      ? `Claimed on ${format(new Date(award.claimedAt), 'MMM d, yyyy')}` 
      : "Pending claim";
  };

  const handleAwardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlayerId || !awardType) {
      toast({
        title: "Missing information",
        description: "Please select a player and award type",
        variant: "destructive",
      });
      return;
    }

    const awardData = {
      playerId: selectedPlayerId,
      awardType,
      details: {},
      reason: reason || undefined,
    };

    // Add type-specific details
    if (awardType === "CURRENCY") {
      if (!currencyType || !amount) {
        toast({
          title: "Missing information",
          description: "Please select currency type and amount",
          variant: "destructive",
        });
        return;
      }
      awardData.details = {
        currencyType,
        amount: Number(amount),
      };
    } else if (awardType === "DECK") {
      if (!selectedDeckId) {
        toast({
          title: "Missing information",
          description: "Please select a deck to award",
          variant: "destructive",
        });
        return;
      }
      awardData.details = {
        deckId: selectedDeckId,
      };
    } else if (awardType === "SHOP_ITEM") {
      if (!selectedShopItemId) {
        toast({
          title: "Missing information",
          description: "Please select a shop item to award",
          variant: "destructive",
        });
        return;
      }
      awardData.details = {
        shopItemId: selectedShopItemId,
      };
    }

    awardMutation.mutate(awardData);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="grant" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="grant" className="flex items-center">
            <Gift className="h-4 w-4 mr-2" />
            Grant Awards
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            Award History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grant">
          <Card>
            <CardHeader>
              <CardTitle>Award Players</CardTitle>
              <CardDescription>
                Grant coins, gems, decks, or shop items to players
              </CardDescription>
            </CardHeader>
            <CardContent>
          <div className="mb-6">
            <div className="flex flex-col space-y-2">
              <Label htmlFor="player-search">Search for player</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  id="player-search"
                  type="text"
                  placeholder="Search by username, email or ID..."
                  className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isLoading && (
                  <div className="absolute right-2 top-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {filteredUsers.length > 0 && !selectedPlayerId && (
            <div className="border rounded-md p-2 mb-6">
              <h3 className="text-sm font-medium mb-2">Search Results:</h3>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filteredUsers.map((user: User) => (
                  <div
                    key={user.id}
                    className="flex justify-between items-center p-2 hover:bg-slate-800 rounded cursor-pointer"
                    onClick={() => handlePlayerSelect(user.id, user.username)}
                  >
                    <div className="flex items-center">
                      <div className="ml-2">
                        <p className="text-sm font-medium">{user.username}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePlayerSelect(user.id, user.username)}
                    >
                      Select
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedPlayerId && (
            <form onSubmit={handleAwardSubmit} className="space-y-4">
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="award-type">Award Type</Label>
                  <Select
                    value={awardType}
                    onValueChange={(value) => setAwardType(value as "CURRENCY" | "DECK" | "SHOP_ITEM")}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select award type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CURRENCY">
                        <div className="flex items-center">
                          <Coins className="h-4 w-4 mr-2" />
                          Currency (Coins/Gems)
                        </div>
                      </SelectItem>
                      <SelectItem value="DECK">
                        <div className="flex items-center">
                          <Package className="h-4 w-4 mr-2" />
                          Deck
                        </div>
                      </SelectItem>
                      <SelectItem value="SHOP_ITEM">
                        <div className="flex items-center">
                          <Gift className="h-4 w-4 mr-2" />
                          Shop Item
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {awardType === "CURRENCY" && (
                  <>
                    <div>
                      <Label htmlFor="currency-type">Currency Type</Label>
                      <Select
                        value={currencyType}
                        onValueChange={(value) => setCurrencyType(value as "COINS" | "GEMS")}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="COINS">Coins</SelectItem>
                          <SelectItem value="GEMS">Gems</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="amount">Amount</Label>
                      <Input
                        id="amount"
                        type="number"
                        min="1"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value ? parseInt(e.target.value) : "")}
                      />
                    </div>
                  </>
                )}

                {awardType === "DECK" && (
                  <div>
                    <Label htmlFor="deck-id">Select Deck</Label>
                    <Select
                      value={selectedDeckId?.toString() || ""}
                      onValueChange={(value) => setSelectedDeckId(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a deck" />
                      </SelectTrigger>
                      <SelectContent>
                        {decks?.map((deck: any) => (
                          <SelectItem key={deck.id} value={deck.id.toString()}>
                            {deck.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {awardType === "SHOP_ITEM" && (
                  <div>
                    <Label htmlFor="shop-item-id">Select Shop Item</Label>
                    <Select
                      value={selectedShopItemId?.toString() || ""}
                      onValueChange={(value) => setSelectedShopItemId(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a shop item" />
                      </SelectTrigger>
                      <SelectContent>
                        {shopItems?.map((item: any) => (
                          <SelectItem key={item.id} value={item.id.toString()}>
                            {item.name} ({item.itemType})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="reason">Reason (Optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for awarding"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    disabled={awardMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!awardType || awardMutation.isPending}
                  >
                    {awardMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Gift className="h-4 w-4 mr-2" />
                        Grant Award
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Award History</CardTitle>
              <CardDescription>
                View all awards granted to players
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isHistoryLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : awardHistory?.awards?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Ban className="h-12 w-12 mx-auto opacity-20 mb-2" />
                  <p>No awards have been granted yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead>Award</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Awarded By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {awardHistory?.awards?.map((award: any) => (
                      <TableRow key={award.id}>
                        <TableCell className="font-medium">
                          {format(new Date(award.createdAt), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          {award.player?.username || "Unknown user"}
                        </TableCell>
                        <TableCell>{formatAwardDetails(award)}</TableCell>
                        <TableCell>{award.reason || "No reason provided"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {award.isClaimed ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Loader2 className="h-4 w-4 text-amber-500" />
                            )}
                            {formatAwardStatus(award)}
                          </div>
                        </TableCell>
                        <TableCell>{award.awardedByAdmin?.username || "Unknown admin"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
